import { useCallback, useId, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocalGatewayOsTabs } from "./LocalGatewayOsTabs";
import { Label } from "@/components/ui/label";
import { getLocalGatewayInstallerMetadata, type LocalGatewayInstallerMetadataResponse } from "@/api/local-gateway-installer";

export function detectInstallerOs(): string {
  if (typeof navigator === "undefined") return "";
  const agent = navigator.userAgent;
  if (/Android|iPhone|iPad|iPod/i.test(agent) ||
      (/Mac/i.test(agent) && navigator.maxTouchPoints > 1)) return "";
  if (/Windows/i.test(agent)) return "windows";
  if (/Mac/i.test(agent)) return "darwin";
  return "";
}

export function LocalGatewayInstallerDownload({ os, onOsChange }: {
  os?: string;
  onOsChange?: (os: string) => void;
}) {
  const { t } = useTranslation("storages");
  const tabId = useId();
  const [detectedOs, setDetectedOs] = useState(detectInstallerOs);
  const installerOs = os ?? detectedOs;
  const setInstallerOs = onOsChange ?? setDetectedOs;
  const metadataRequest = useRef<object | null>(null);
  useEffect(() => () => { metadataRequest.current = null; }, []);
  const [installerTarget, setInstallerTarget] = useState("");
  const [installerMetadata, setInstallerMetadata] =
    useState<LocalGatewayInstallerMetadataResponse | null>(null);
  const [installerMetadataLoading, setInstallerMetadataLoading] = useState(false);
  const [installerDownloading, setInstallerDownloading] = useState(false);
  const [installerDownloadError, setInstallerDownloadError] = useState(false);
  const installerDownloadRequest = useRef<object | null>(null);
  useEffect(() => {
    setInstallerDownloading(false);
    setInstallerDownloadError(false);
    return () => { installerDownloadRequest.current = null; };
  }, []);
  const loadInstallerMetadata = useCallback(async () => {
    const request = {};
    metadataRequest.current = request;
    setInstallerMetadataLoading(true);
    setInstallerMetadata(null);
    try {
      const metadata = await getLocalGatewayInstallerMetadata();
      if (metadataRequest.current === request) setInstallerMetadata(metadata);
    } catch {
      if (metadataRequest.current === request) setInstallerMetadata({ state: "unavailable", failureClass: "bff_unavailable" });
    } finally {
      if (metadataRequest.current === request) setInstallerMetadataLoading(false);
    }
  }, []);
  const downloadInstaller = async (target: string, packageFormat: string) => {
    if (installerDownloadRequest.current) return;
    const request = {};
    installerDownloadRequest.current = request;
    setInstallerDownloading(true);
    setInstallerDownloadError(false);
    try {
      // A displayed URL may already have expired. Resolve the selected target again
      // immediately before navigation, including any intervening stable release change.
      const metadata = await getLocalGatewayInstallerMetadata();
      if (installerDownloadRequest.current !== request) return;
      setInstallerMetadata(metadata);
      const artifact = metadata.state === "available"
        ? metadata.targets.find(item => item.target === target)?.artifacts.find(item => item.packageFormat === packageFormat)
        : undefined;
      if (!artifact) throw new Error("installer_download_unavailable");
      const link = document.createElement("a");
      link.href = artifact.downloadUrl;
      link.download = artifact.fileName;
      link.referrerPolicy = "no-referrer";
      document.body.append(link);
      try { link.click(); } finally { link.remove(); }
    } catch {
      if (installerDownloadRequest.current === request) setInstallerDownloadError(true);
    } finally {
      if (installerDownloadRequest.current === request) {
        installerDownloadRequest.current = null;
        setInstallerDownloading(false);
      }
    }
  };
  useEffect(() => {
    if (installerMetadata === null && !installerMetadataLoading) {
      void loadInstallerMetadata();
    }
  }, [installerMetadata, installerMetadataLoading, loadInstallerMetadata]);
  const osTargets = installerMetadata?.state === "available"
    ? installerMetadata.targets.filter(target => target.target.startsWith(`${installerOs}-`))
    : [];
  const visibleInstallerTargets = osTargets.length === 1
    ? osTargets
    : osTargets.filter(target => target.target === installerTarget);
  return (
        <div className="space-y-5 border-t border-slate-200 pt-4 text-sm leading-6 text-slate-700">
          <LocalGatewayOsTabs tabId={tabId} label={t("new.installerOs")} selectedOs={installerOs}
            disabled={installerDownloading} onChange={os => { setInstallerOs(os); setInstallerTarget(""); }} />
          <div role="tabpanel" id={`${tabId}-panel`} aria-labelledby={installerOs ? `${tabId}-${installerOs}` : undefined} className="space-y-5" tabIndex={0}>
          {!installerOs ? <p>{t("new.installerSelectOs")}</p> : null}
          {installerDownloadError ? <p role="alert" className="text-destructive">{t("new.localGatewayInstallerDownloadFailed")}</p> : null}
          {installerDownloading ? <p role="status">{t("new.localGatewayInstallerMetadataLoading")}</p> : null}
          {installerMetadataLoading ? (
            <p role="status">{t("new.localGatewayInstallerMetadataLoading")}</p>
          ) : installerMetadata?.state === "available" ? (
            <div className="space-y-4">
              {osTargets.length > 1 ? (
                <div className="space-y-2">
                  <Label htmlFor="gateway-installer-target">{t("new.installerArchitecture")}</Label>
                  <select id="gateway-installer-target" className="block w-full rounded-md border border-input bg-background p-2 sm:w-72"
                    value={installerTarget} disabled={installerDownloading}
                    onChange={event => setInstallerTarget(event.target.value)}>
                    <option value="">{t("new.installerSelectArchitecture")}</option>
                    {osTargets.map(target => <option key={target.target} value={target.target}>{t(`new.localGatewayInstallerTargets.${target.target}`)}</option>)}
                  </select>
                </div>
              ) : null}
              {installerOs && osTargets.length === 0 ? <p role="status">{t("new.installerOsUnavailable")}</p> : null}
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-slate-600">
                <p>{t("new.localGatewayInstallerVersion", { version: installerMetadata.version })}</p>
                {installerOs === "darwin" && osTargets.length > 0 ? <p>{t("new.installerMacCompatibility")}</p> : null}
              </div>
              <div className="space-y-3">
                {visibleInstallerTargets.map((target) => (
                  <div key={target.target} className="min-w-0 space-y-2">
                    {target.artifacts.map((artifact) => (
                      <div key={`${target.target}:${artifact.packageFormat}`} className="space-y-1">
                        <Button type="button" variant="outline"
                          className="h-auto w-full justify-start whitespace-normal py-3 text-left"
                          disabled={installerDownloading}
                          onClick={() => void downloadInstaller(target.target, artifact.packageFormat)}>
                          <Download className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                          {t("new.localGatewayInstallerTargetDownloadCta", {
                            target: t(`new.localGatewayInstallerTargets.${target.target}`),
                            format: target.artifacts.length > 1 ? `（${artifact.packageFormat.toUpperCase()}）` : "",
                          })}
                          <span className="ml-2 shrink-0 text-xs font-normal text-slate-500">
                            （{t("new.localGatewayInstallerSize", { size: Math.ceil(artifact.sizeBytes / 1024 / 1024) })}）
                          </span>
                        </Button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {visibleInstallerTargets.filter(target => target.nativeSigningState !== "signed").map((target) => (
                <p key={target.target} className="text-xs text-slate-600">
                  {t(`new.localGatewayInstallerTargets.${target.target}`)}: {t(`new.localGatewayInstallerSigning.${target.nativeSigningState}`)}
                </p>
              ))}
            </div>
          ) : installerMetadata?.state === "unavailable" ? (
            <div className="space-y-2">
              <p>{t("new.localGatewayInstallerMetadataUnavailable")}</p>
              <Button type="button" variant="outline" size="sm" onClick={() => void loadInstallerMetadata()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t("new.localGatewayInstallerMetadataRefresh")}
              </Button>
            </div>
          ) : null}
          </div>
        </div>
  );
}
